"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { FORM_TOKEN_ENDPOINT, HONEYPOT_FIELD } from "@/lib/anti-spam-shared";

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileOptions = {
  sitekey: string;
  callback: (token: string) => void;
  "error-callback": () => void;
  "expired-callback": () => void;
  appearance?: "always" | "execute" | "interaction-only";
  action?: string;
  theme?: "light" | "dark" | "auto";
};

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: TurnstileOptions) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Turnstile script failed to load"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export type FormGuard = {
  setContainer: (el: HTMLDivElement | null) => void;
  setHoneypot: (el: HTMLInputElement | null) => void;
  unavailable: boolean;
  waitUntilReady: (timeoutMs?: number) => Promise<void>;
  payload: () => Record<string, string>;
  reset: () => void;
};

export function useFormGuard(action?: string): FormGuard {
  const [unavailable, setUnavailable] = useState(false);
  const [cycle, setCycle] = useState(0);

  const honeypotRef = useRef<HTMLInputElement | null>(null);
  const setHoneypot = useCallback((el: HTMLInputElement | null) => {
    honeypotRef.current = el;
  }, []);
  const widgetIdRef = useRef<string | null>(null);
  const formTokenRef = useRef<string | null>(null);
  const turnstileTokenRef = useRef<string | null>(null);
  const settledRef = useRef({ token: false, turnstile: !SITE_KEY });

  useEffect(() => {
    let cancelled = false;
    settledRef.current.token = false;

    fetch(FORM_TOKEN_ENDPOINT, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("failed"))))
      .then((data: { token?: string | null }) => {
        if (cancelled) return;
        formTokenRef.current = typeof data.token === "string" ? data.token : null;
      })
      .catch(() => {
        // The route rejects a missing token visibly, so the user can retry.
      })
      .finally(() => {
        if (!cancelled) settledRef.current.token = true;
      });

    return () => {
      cancelled = true;
    };
  }, [cycle]);

  const setContainer = useCallback(
    (el: HTMLDivElement | null) => {
      if (!SITE_KEY || !el) return;

      let detached = false;
      settledRef.current.turnstile = false;

      loadTurnstile()
        .then(() => {
          if (detached || !window.turnstile) return;
          widgetIdRef.current = window.turnstile.render(el, {
            sitekey: SITE_KEY,
            action,
            appearance: "interaction-only",
            theme: "light",
            callback: (token) => {
              turnstileTokenRef.current = token;
              settledRef.current.turnstile = true;
            },
            "error-callback": () => {
              turnstileTokenRef.current = null;
              settledRef.current.turnstile = true;
              setUnavailable(true);
            },
            "expired-callback": () => {
              turnstileTokenRef.current = null;
            },
          });
        })
        .catch(() => {
          if (detached) return;
          settledRef.current.turnstile = true;
          setUnavailable(true);
        });

      return () => {
        detached = true;
        const id = widgetIdRef.current;
        widgetIdRef.current = null;
        if (id) window.turnstile?.remove(id);
      };
    },
    [action],
  );

  const waitUntilReady = useCallback(async (timeoutMs = 8_000) => {
    const deadline = performance.now() + timeoutMs;
    while (performance.now() < deadline) {
      const { token, turnstile } = settledRef.current;
      if (token && turnstile) return;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }, []);

  const payload = useCallback(() => {
    const fields: Record<string, string> = {
      [HONEYPOT_FIELD]: honeypotRef.current?.value ?? "",
    };
    if (formTokenRef.current) fields.formToken = formTokenRef.current;
    if (turnstileTokenRef.current) {
      fields.turnstileToken = turnstileTokenRef.current;
    }
    return fields;
  }, []);

  const reset = useCallback(() => {
    turnstileTokenRef.current = null;
    formTokenRef.current = null;
    if (honeypotRef.current) honeypotRef.current.value = "";
    setUnavailable(false);

    const id = widgetIdRef.current;
    if (id && window.turnstile) {
      settledRef.current.turnstile = false;
      window.turnstile.reset(id);
    } else {
      settledRef.current.turnstile = true;
    }

    setCycle((n) => n + 1);
  }, []);

  return useMemo(
    () => ({
      setContainer,
      setHoneypot,
      unavailable,
      waitUntilReady,
      payload,
      reset,
    }),
    [setContainer, setHoneypot, unavailable, waitUntilReady, payload, reset],
  );
}

export function FormGuard({ guard }: { guard: FormGuard }) {
  const id = useId();
  const { setHoneypot, setContainer, unavailable } = guard;

  return (
    <>
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label htmlFor={id}>Website</label>
        <input
          id={id}
          ref={setHoneypot}
          type="text"
          name={HONEYPOT_FIELD}
          tabIndex={-1}
          autoComplete="off"
          defaultValue=""
        />
      </div>

      <div ref={setContainer} />

      {unavailable && (
        <p className="text-caption text-brand-orange-02">
          We couldn&apos;t load the security check. Please refresh the page, or disable any content
          blocker and try again.
        </p>
      )}
    </>
  );
}
